import type { ShipSnapshot, WorldSnapshot } from "@frontier/protocol/contracts";
import { regionRisk } from "@frontier/content/risk";
import { confirmedShipImpacts } from "../game/combat-vfx-presentation";
import type { MusicScene, SoundCue, SoundId } from "./sound-catalog";
import { weaponSound } from "./sound-catalog";

export interface AudioFrame {
  cues: SoundCue[];
  music: MusicScene;
  engine: number;
}
const silent = (): AudioFrame => ({ cues: [], music: "station", engine: 0 });

/** Own-ship sound only; remote combat can influence the non-diegetic score. */
export class SnapshotAudio {
  private previous: WorldSnapshot | null = null;
  private receivedAt = -Infinity;
  private identity = "";
  private combatUntil = 0;

  reset(): void {
    this.previous = null;
    this.receivedAt = -Infinity;
    this.identity = "";
    this.combatUntil = 0;
  }

  receive(world: WorldSnapshot, playerId: string, runId: string, receivedAt: number): AudioFrame {
    const identity = `${playerId}:${runId}`;
    const oldWorld = this.identity === identity ? this.previous : null;
    if (oldWorld && receivedAt <= this.receivedAt) return this.frame(world, playerId, []);
    const continuous = oldWorld && world.tick > oldWorld.tick && world.time >= oldWorld.time &&
      receivedAt - this.receivedAt < 1000;
    if (!continuous) this.combatUntil = 0;
    this.identity = identity;
    this.previous = world;
    this.receivedAt = receivedAt;
    const pilot = world.players.find((ship) => ship.id === playerId);
    const oldPilot = oldWorld?.players.find((ship) => ship.id === playerId);
    if (!continuous || !pilot || !oldPilot) return this.frame(world, playerId, []);
    const sameFlight = (oldPilot.controls?.flightId ?? null) === (pilot.controls?.flightId ?? null);
    // A replacement flight is a baseline; real dock/launch boundaries still have their own cues.
    if (!sameFlight && !oldPilot.docked && !pilot.docked) {
      this.combatUntil = 0;
      return this.frame(world, playerId, []);
    }
    const cues: SoundCue[] = [];
    const local = (id: SoundId, gain = 1) => cues.push({ id, gain, pan: 0 });
    if (sameFlight && !oldPilot.dead && pilot.dead) local("explosion");
    else if (!pilot.dead && !oldPilot.dead) {
      if (oldPilot.docked && !pilot.docked) local("launch", 0.7);
      else if (!oldPilot.docked && pilot.docked) local("dock", 0.6);
      else if (!pilot.docked && oldPilot.sectorId !== pilot.sectorId) local("jump", 0.75);
    }
    const flight = !pilot.dead && !pilot.docked && !oldPilot.dead && !oldPilot.docked &&
      oldPilot.sectorId === pilot.sectorId && oldPilot.hullId === pilot.hullId &&
      sameFlight &&
      Math.hypot(pilot.x - oldPilot.x, pilot.y - oldPilot.y) <= 300;
    if (!flight) {
      this.combatUntil = 0;
      return this.frame(world, playerId, cues);
    }
    const ships = [...world.players, ...world.npcs];
    const previousShips = new Map([...oldWorld.players, ...oldWorld.npcs].map((ship) => [ship.id, ship]));
    for (const impact of confirmedShipImpacts(previousShips, ships)) {
      if (impact.shipId === playerId) local(impact.kind, 0.75);
      if (Math.hypot(impact.x - pilot.x, impact.y - pilot.y) < 650)
        this.combatUntil = world.time + 7;
    }
    const oldShots = new Set(oldWorld.projectiles.map((shot) => shot.id));
    const volleys = new Set<string>();
    for (const shot of world.projectiles) {
      if (oldShots.has(shot.id)) continue;
      const owner = ships.find((ship) => ship.id === shot.ownerId);
      // An already travelling projectile entering AOI is not a new shot.
      if (!owner || owner.dead || owner.docked || owner.sectorId !== pilot.sectorId ||
        !previousShips.has(owner.id) || Math.hypot(shot.x - owner.x, shot.y - owner.y) > 220) continue;
      const volley = `${shot.ownerId}:${shot.kind}`;
      if (volleys.has(volley)) continue;
      volleys.add(volley);
      if (owner.id === playerId) local(weaponSound(shot.kind), 0.6);
      if (shot.kind !== "mine" && (owner.targetId === playerId ||
        (owner.id === playerId && ships.some(target => target.id === owner.targetId))))
        this.combatUntil = world.time + 7;
    }
    if (pilot.cargo > oldPilot.cargo) local("pickup", 0.65);
    for (const [module, oldModule, cooldown, oldCooldown] of [
      [pilot.moduleId, oldPilot.moduleId, pilot.abilityCooldown, oldPilot.abilityCooldown],
      [pilot.secondaryModuleId, oldPilot.secondaryModuleId, pilot.secondaryCooldown, oldPilot.secondaryCooldown],
    ] as const) {
      if (module === oldModule && cooldown > oldCooldown + 0.1)
        local(module === "repair" || module === "repair-field" ? "repair" : "ability", 0.6);
    }
    if (pilot.maxHp === oldPilot.maxHp && pilot.hp > 0 && pilot.hp / pilot.maxHp <= 0.25 &&
      oldPilot.hp / oldPilot.maxHp > 0.25) local("warning", 0.75);
    return this.frame(world, playerId, cues);
  }

  private frame(world: WorldSnapshot, playerId: string, cues: SoundCue[]): AudioFrame {
    const pilot = world.players.find((ship) => ship.id === playerId);
    if (!pilot || pilot.dead || pilot.docked) return { ...silent(), cues };
    return {
      cues, music: world.time < this.combatUntil ? "combat" :
        regionRisk(pilot.sectorId) === "safe-pve" ? "exploration" : "danger",
      engine: pilot.boosting ? 0.3 : 0.09,
    };
  }
}
